/**
 * Batch runners for agents built on the template, keyed by behavior.
 *
 * The definition (src/lib/ai/agentDefinitions.ts) says what an agent is; this
 * map says what runs when the hub or the settings page presses "Run now". Kept
 * apart from the definitions so the definitions stay free of runtime imports.
 * A custom agent runs its behavior's runner under its own agent type and name.
 */

import type { BatchStats, RunScope } from './agentRunService';
import type { AgentBehaviorKind, AgentDefinition } from './ai/agentDefinitions';
import { runValueEstimatorBatch, type EstimateAgent } from './valueEstimatorRunner';
import { runClassifyBatch } from './classifyRunner';
import { runClusterBatch } from './clusterRunner';
import { runRelationshipBatch } from './relationshipRunner';
import { INTELLIGENCE_AGENTS } from './intelligence/agentMeta';
import { getAgentSettings } from './agentSettingsService';

export type TemplateBatchRunner = (
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
  runByUserId?: string | null,
) => Promise<BatchStats>;

type BehaviorBatchRunner = (
  agent: EstimateAgent,
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
  runByUserId?: string | null,
) => Promise<BatchStats>;

// Only behaviors that run over a scope have one; draft_items runs only when a person uses it.
const BEHAVIOR_BATCH_RUNNERS: Partial<Record<AgentBehaviorKind, BehaviorBatchRunner>> = {
  estimate_value: (agent, workspaceId, scope, onProgress, signal, runByUserId) =>
    runValueEstimatorBatch(workspaceId, scope, onProgress, signal, runByUserId, agent),
  classify: (agent, workspaceId, scope, onProgress, signal, runByUserId) =>
    runClassifyBatch(workspaceId, scope, onProgress, signal, runByUserId, agent),
  detect_cluster: (agent, workspaceId, scope, onProgress, signal, runByUserId) =>
    runClusterBatch(workspaceId, scope, onProgress, signal, runByUserId, agent),
  propose_relationship: (agent, workspaceId, scope, onProgress, signal, runByUserId) =>
    runRelationshipBatch(workspaceId, scope, onProgress, signal, runByUserId, agent),
  // Each finding agent runs its own detector over the whole workspace (src/lib/intelligence).
  find_issues: async (agent, workspaceId, scope, onProgress, signal) => {
    const meta = INTELLIGENCE_AGENTS[agent.agentType];
    const { data: settings } = await getAgentSettings(workspaceId, agent.agentType);
    if (!meta || !settings?.enabled) return { processed: 0, proposed: 0, skipped: 0, errors: 0 };
    return meta.batchRunner(workspaceId, scope, settings, onProgress, signal);
  },
};

/** Whether this agent's behavior can run over a scope (Run now, batch, the run panel). */
export function hasTemplateBatchRunner(def: AgentDefinition): boolean {
  return !!BEHAVIOR_BATCH_RUNNERS[def.behaviorKind];
}

/**
 * Behaviors whose runner works on exactly the items it is given. The finding
 * agents (find_issues) read the whole workspace whatever they are given, so
 * they are not offered for "Ask [agent] for these N" on the items list.
 */
const RUNS_OVER_CHOSEN_ITEMS: ReadonlySet<AgentBehaviorKind> = new Set(['estimate_value', 'classify', 'detect_cluster', 'propose_relationship']);

/** Whether this agent can be asked about a set of items a person chose (the items list's bulk bar). */
export function runsOverChosenItems(def: AgentDefinition): boolean {
  return hasTemplateBatchRunner(def) && RUNS_OVER_CHOSEN_ITEMS.has(def.behaviorKind);
}

/** The "Run now" runner for any template agent, built-in or custom. Callers check hasTemplateBatchRunner first. */
export function templateBatchRunnerFor(def: AgentDefinition): TemplateBatchRunner {
  const run = BEHAVIOR_BATCH_RUNNERS[def.behaviorKind];
  if (!run) throw new Error(`${def.name} does not run over a scope.`);
  const agent: EstimateAgent = { agentType: def.agentType, name: def.name };
  return (workspaceId, scope, onProgress, signal, runByUserId) =>
    run(agent, workspaceId, scope, onProgress, signal, runByUserId);
}
